/**
 * @file WorldState.ts
 * @description Core ECS implementation using Jotai. Organized into:
 *  - Section 1: Imports & Constants
 *  - Section 2: Utility Types (Prettify, ValueOf, _devProp)
 *  - Section 3: Utils Namespace – Tag Classes and Factories
 *  - Section 4: World Namespace – Uniques, Components, Entities, and Helpers
 *  - Section 5: WorldState Interface and UID Exports
 */

//
// -------------------------------------------------------------------------------------
// Section 1: Imports & Fundamental Constants
// -------------------------------------------------------------------------------------
import type { DevString, Handler, IPool } from "@phosphor/utils";
import { invariant, whenStr } from "@phosphor/utils";
import type { DisposePool } from "@phosphor/utils";
import { type Atom, atom } from "jotai";
import type { ILogger } from "librarylog";
import type { JotaiStore } from "../lib/jotai/jotai-types.ts";
import { pipeNonNull } from "../lib/js/pipeNonNull.ts";
import type { Id, PhosphorObjectID } from "../vmc/livestore-stubs.ts";
import type * as Loro from "../vmc/loro-stubs.ts";

export const Target = Symbol.for("@phosphor/ui/Target");
export const Key = Symbol.for("@phosphor/ui/Key");

//
// -------------------------------------------------------------------------------------
// Section 2: Basic Utility Types and Helpers
// -------------------------------------------------------------------------------------
type Prettify<T> = { [K in keyof T]: T[K] } & {};

export interface ValueOf<ClassType> {
  readonly [Key]: string;
}
type ValueType<ClassType extends { Type: any }> = ClassType["Type"];

interface _devProp {
  _dev?: { source?: DevString; [other: string]: unknown };
}

//
// -------------------------------------------------------------------------------------
// Section 3: The Utils Namespace – Tag Classes and Helper Factories
// -------------------------------------------------------------------------------------
export namespace Utils {
  export type AllowedType = object | Array<unknown>;
  const valueOf = <T extends AllowedType>(key: string, x: T) => Object.assign(x, { [Key]: key });

  /**
   * TagClass defines a tagged class with a key and associated type.
   * Foundation for Components, Uniques, and Entities.
   * @example
   * class MyComponent extends World.Component("myComponent")<MyComponent, { foo: number }>() {}
   * const instance = MyComponent.of({ foo: 123 });
   */
  export interface TagClass<Self, Key extends string, Type extends AllowedType> {
    readonly key: Key;
    readonly name: never;
    readonly CreationType: Type & _devProp;
    readonly Type: ValueOf<Self> & Type & _devProp;
    readonly Opaque: ValueOf<Self>;
    new (_: never): { readonly Key: Key; readonly Type: ValueOf<Self> & Type };
    of(value: Type & _devProp): ValueOf<Self> & Type;
    getFromObject<T extends Partial<Record<Key, unknown>>>(obj: T): T[Key];
    getFromObject(obj: object): Type;
  }
  export type AnyTagClass = TagClass<any, string, any>;
  export type AnyTagClasses = [AnyTagClass, ...AnyTagClass[]];

  /**
   * Creates a base tagged class.
   */
  export const Tag =
    <const Key extends string>(key: Key) =>
    <Self, Type extends AllowedType>(): TagClass<Self, Key, Type> =>
      class {
        static readonly key = key;
        static readonly id = key;
        static of = valueOf.bind(null, key);
        // eslint-disable-next-line @typescript-eslint/no-implied-eval
        static getFromObject = new Function("obj", `return obj[${JSON.stringify(key)}]`);
      } as any;

  export type TagCreationValues<Tags extends [AnyTagClass, ...AnyTagClass[]]> = Prettify<{
    [K in Tags[number] as K extends AnyTagClass ? K["key"] : never]: K extends AnyTagClass ? K["Opaque"] : never;
  }>;
  export type TagValues<Tags extends [AnyTagClass, ...AnyTagClass[]]> = {
    [K in Tags[number] as K["key"]]: ValueType<K>;
  };
}

//
// -------------------------------------------------------------------------------------
// Section 4: The World Namespace – Uniques, Components, Entities, and Helpers
// -------------------------------------------------------------------------------------
export namespace World {
  // ---------- Uniques ----------
  /**
   * Creates a unique singleton class. Only one instance per WorldState.
   * @example
   * class USettings extends World.Unique("settings")<USettings, {
   *   themeAtom: Atom<"light" | "dark">;
   *   fontSizeAtom: Atom<number>;
   * }>() {}
   *
   * // In plugin setup:
   * build.addUnique(USettings, USettings.of({
   *   themeAtom: atom("light"),
   *   fontSizeAtom: atom(14)
   * }));
   *
   * // Later usage:
   * const settings = world.getUniqueOrThrow(USettings);
   */
  export const Unique =
    <Key extends string>(key: Key) =>
    <Self, Type extends Utils.AllowedType>(): UniqueClass<Self, Key, Type> =>
      Utils.Tag(key)<Self, Type>();
  export type AnyUniqueClass<Type extends Utils.AllowedType = any> = UniqueClass<any, string, Type>;
  export type AnyUniques = [AnyUniqueClass, ...AnyUniqueClass[]];
  export interface UniqueClass<Self, Key extends string, Type extends Utils.AllowedType>
    extends Utils.TagClass<Self, Key, Type> {
    readonly key: Key;
  }

  // ---------- Components ----------
  /**
   * Creates a component class that can be attached to entities.
   * Components provide behavior and data to entities.
   * @example
   * class CMoveable extends World.Component("moveable")<CMoveable, {
   *   maxSpeedAtom: Atom<number>;
   *   directionAtom: Atom<"up" | "down" | "left" | "right">;
   * }>() {}
   *
   * // Attach to entity:
   * world.addEntity(uid, PlayerEntity, {
   *   moveable: CMoveable.of({
   *     maxSpeedAtom: atom(100),
   *     directionAtom: atom("up")
   *   })
   * });
   *
   * // Access component:
   * const moveable = world.getComponentMaybe(uid, CMoveable);
   */
  export const Component =
    <Key extends string>(key: Key) =>
    <Self, Type extends Utils.AllowedType>(): ComponentClass<Self, Key, Type> =>
      Utils.Tag(key)<Self, Type>();
  export type AnyComponentClass<Type extends Utils.AllowedType = any> = ComponentClass<any, any, Type>;
  export type AnyComponents = [AnyComponentClass, ...AnyComponentClass[]];
  export interface ComponentClass<Self, Key extends string, Type extends Utils.AllowedType>
    extends Utils.TagClass<Self, Key, Type> {
    readonly key: Key;
  }

  // ---------- Entity & UID Helpers ----------
  export type AnyTarget = undefined | AnyEntityClass | [AnyComponentClass, ...AnyComponentClass[]];
  export type UnknownComponentValues = Record<string, unknown>;
  export type ComponentValues<Cs extends AnyComponents> = Utils.TagValues<Cs>;
  export type ComponentCreationValues<Cs extends AnyComponents> = Utils.TagCreationValues<Cs>;
  export type TargetComponentValues<Target> = undefined extends Target
    ? {}
    : Target extends [AnyComponentClass, ...AnyComponentClass[]]
      ? ComponentValues<Target>
      : Target extends Utils.TagClass<any, any, infer T>
        ? T
        : never;

  /**
   * UID is an opaque string identifier of the form "containerId|treeID|key".
   * Type parameter specifies which entity/components this UID points to.
   * @example
   * type PlayerUID = UID<typeof PlayerEntity>;
   * type MovableUID = UID<[typeof CMoveable]>;
   *
   * const uid: UID<undefined> = World.uid(null, null, "player-1");
   * const playerUID: UID<typeof PlayerEntity> = uid as UID<typeof PlayerEntity>;
   */
  export type UID<TargetEntity extends AnyTarget = undefined> = `${string}|${string}|${string}` & {
    [Target]: TargetEntity extends undefined
      ? {}
      : Prettify<
          TargetEntity extends AnyEntityClass
            ? EntityComponentIDs<TargetEntity>
            : TargetEntity extends [AnyComponentClass, ...AnyComponentClass[]]
              ? ComponentIDs<TargetEntity>
              : never
        >;
  };
  export namespace UID {
    export function withLocalID(containerUID: UID, itemId: Id, key?: string | undefined) {
      const parsed = parseUID(containerUID);
      return uid(parsed.containerId, itemId, key || null);
    }
  }

  /**
   * Extends an existing UID by updating the key.
   */
  export function uidWith(uidInput: UID, key: string) {
    const parsed = parseUID(uidInput);
    return uid(parsed.loroContainerID, parsed.loroTreeID, `${whenStr(parsed.key, (a) => `${a}-`)}${key}`);
  }

  type ComponentIDs<Components extends AnyComponents> = {
    [K in keyof ComponentValues<Components>]: true;
  };

  type EntityComponentIDs<TargetEntity extends AnyEntityClass> = {
    [K in keyof EntityReturnValue<TargetEntity["components"], TargetEntity["componentsProvidedByPlugins"]>]: true;
  };

  const not_ = <T extends string>(s: string | null | undefined | false) => (!s || s === "_" ? null : (s as T));

  export const isUID = (str: string): str is UID => /^[^|]+\|[^|]+\|[^|]+$/.test(str);

  type ContainerID = string | PhosphorObjectID | Loro.ContainerID;
  type ItemId = Id | Loro.TreeID;

  export const parseUID = (uid: UID) => {
    invariant(typeof uid === "string", "UID is expected to be a string", { uid });
    const [containerId, itemId, string] = uid.split("|");
    return {
      containerId: not_<ContainerID>(containerId),
      itemId: not_<Id>(itemId),
      phosphorObjectID: not_<PhosphorObjectID>(containerId?.includes("/") && containerId),
      loroContainerID: not_<Loro.ContainerID>(containerId?.startsWith("cid:") && containerId),
      loroTreeID: not_<Loro.TreeID>(itemId?.includes("@") && itemId),
      key: not_<string>(string),
    };
  };

  type EntityReturnValue<Cs extends AnyComponents, Ps extends AnyComponents | []> = Prettify<
    ComponentValues<Cs> & (Ps extends AnyComponents ? ComponentValues<Ps> : {})
  >;

  export type EntityBundle<T extends AnyTarget = AnyComponents> = EntityInfo & TargetComponentValues<T>;

  /**
   * Creates a UID string.
   *
   */
  export const uid = (containerId: ContainerID | null, itemId: ItemId | null, key: string | null) =>
    `${containerId || "_"}|${itemId || "_"}|${key || "_"}` as UID<undefined>;

  // ---------- Helper Components ----------
  /**
   * Creates a component that links to another entity.
   */
  export const ComponentForUID =
    <const Id extends string>(id: Id) =>
    <Self>(): ComponentClass<Self, Id, { uidAtom: Atom<World.UID | null> }> =>
      Component(id)<Self, { uidAtom: Atom<World.UID | null> }>();
  export type AnyUIDComponentClass = ComponentClass<any, any, { uidAtom: Atom<World.UID | null> }>;

  /**
   * Creates a component for a handler.
   */
  export const ComponentForHandler =
    <const Id extends string>(id: Id) =>
    <Self, T>(): ComponentClass<Self, Id, { handler: Handler<T> }> =>
      Component(id)<Self, { handler: Handler<T> }>();
  export type AnyHandlerComponentClass = ComponentClass<any, any, { handler: Handler<any> }>;

  /**
   * Get an entity's component via a UID link.
   */
  export function getComponentAtomOf<T extends World.AnyComponentClass>(
    world: WorldState,
    start: World.UID,
    lookupComponent: AnyUIDComponentClass,
    component: T,
  ): Atom<T["Type"] | undefined | null> {
    const lookupUIDAtom = world.getComponentAtom(start, lookupComponent);
    return atom((get) =>
      pipeNonNull(lookupUIDAtom, get, ({ uidAtom }) => get(uidAtom), world.getComponentAtom(component), get),
    );
  }

  // ---------- Entity Creation & Plugin Helpers ----------
  const keepDuplicatesFilter = <T, U>(fn: (t: T) => U) => {
    const seen = new Set<U>();
    return (s: T) => {
      const u = fn(s);
      if (seen.has(u)) return true;
      seen.add(u);
      return false;
    };
  };

  /**
   * Creates an entity class that groups required components.
   * Second array is for components added by plugins.
   * @example
   * class PlayerEntity extends World.Entity("player",
   *   [CMoveable, CHealth], // Required components
   *   [CRenderable]         // Added by plugins (optional)
   * )<PlayerEntity>() {}
   *
   * // Create entity with required components:
   * const playerUID = world.addEntity(uid, PlayerEntity, {
   *   moveable: CMoveable.of({ maxSpeedAtom: atom(100) }),
   *   health: CHealth.of({ currentAtom: atom(100) })
   * });
   */
  export const Entity =
    <
      const Id extends string,
      const Components extends AnyComponents,
      const PluginComponents extends AnyComponents | [] = [],
    >(
      id: Id,
      options: {
        components: Components;
        componentsProvidedByPlugins?: PluginComponents;
      },
    ) =>
    <Self>(): EntityClass<Self, Id, Components, PluginComponents> => {
      const duplicates = options.components.filter(keepDuplicatesFilter((c) => c.key)).map((c) => c.key);
      invariant(duplicates.length === 0, "Component keys must be unique", {
        components: options.components,
        duplicates,
      });
      return Object.assign(
        Utils.Tag(id)<Self, EntityBundle<EntityClass<Self, Id, Components, PluginComponents>>>(),
        options,
      ) as any;
    };

  export type EntityCreationValues<Target extends AnyEntityClass> = Target extends AnyEntityClass
    ? Utils.TagCreationValues<Target["components"]>
    : never;

  export interface EntityClass<
    Self,
    in out Id extends string,
    in out Components extends AnyComponents = AnyComponents,
    in out PluginComponents extends AnyComponents | [] = [],
  > extends Utils.TagClass<
      Self,
      Id,
      ComponentValues<Components> & (PluginComponents extends AnyComponents ? ComponentValues<PluginComponents> : [])
    > {
    readonly components: Components;
    readonly componentsProvidedByPlugins: PluginComponents;
  }

  export type AnyEntityClass = Utils.TagClass<any, any, any> & {
    components: AnyComponents;
    componentsProvidedByPlugins: AnyComponents | [];
  };

  /**
   * Options for entity creation callbacks in plugins.
   * Automatically provides additional components when entities are created.
   * @example
   * // When any entity with CWorkspaceEntityTag is created, also add CLabel:
   * build.onEntityCreated(
   *   { requires: [CWorkspaceEntityTag], provides: [CLabel] },
   *   // We use a tagged component to identify the entity. Tags don't have values.
   *   (uid, {}) => ({
   *     label: CLabel.of({ textAtom: constAtom(`Workspace(${uid})`) })
   *   })
   * );
   * @example
   * // When any entity with CTransform is created, also add CLabel:
   * build.onEntityCreated(
   *   { requires: [CTransform], provides: [CLabel] },
   *   (uid, { transform }) => ({
   *     label: CLabel.of({ textAtom: atom((get) => `@${get(transform.xAtom)},${get(transform.yAtom)}`) })
   *   })
   * );
   */
  export type AnyEntityCreatedFnOptions = {
    requires: World.AnyComponents;
    optional?: World.AnyComponents | [];
    provides?: World.AnyComponents | [];
  };

  type ProviderReturn<T extends AnyEntityCreatedFnOptions> = T["provides"] extends World.AnyComponents
    ? World.ComponentCreationValues<NonNullable<T["provides"]>>
    : void;

  export type EntityCreatedCallback<O extends AnyEntityCreatedFnOptions> = (
    uid: UID,
    entityComponents: EntityCreatedCallbackComponents<O>,
    world: WorldState,
    onDeath: IPool,
    define: (values: ProviderReturn<O>) => ProviderReturn<O>,
  ) => ProviderReturn<O>;

  export type EntityCreatedCallbackComponents<O extends AnyEntityCreatedFnOptions> = World.ComponentValues<
    O["requires"]
  > &
    (O["optional"] extends World.AnyComponents ? Partial<World.ComponentValues<NonNullable<O["optional"]>>> : {});

  export type EntityCreatedCallback2<O extends AnyEntityCreatedFnOptions> = (
    entityComponents: EntityInfo &
      Prettify<
        World.ComponentValues<O["requires"]> &
          (O["optional"] extends World.AnyComponents ? Partial<World.ComponentValues<NonNullable<O["optional"]>>> : {})
      >,
    world: WorldState,
    define: (values: ProviderReturn<O>) => ProviderReturn<O>,
  ) => ProviderReturn<O>;

  /**
   * Plugin interface for extending WorldState functionality.
   * Plugins can add uniques, react to entity creation, and add behavior.
   * @example
   * const MyPlugin = World.definePlugin({
   *   name: dev`MyPlugin`.ctx({ loc: "phosphor-blog/scripts/vmc/WorldState.ts:416:14" }),
   *   setup(build) {
   *     // Add singleton service:
   *     build.addUnique(UMyService, UMyService.of({ }));
   *
   *     // Auto-add components:
   *     build.onEntityCreated(
   *       { requires: [CTransform], provides: [CLabel] },
   *       (uid, components) => ({
   *         label: CLabel.of({ textAtom: atom("Entity") })
   *       })
   *     );
   *   }
   * });
   */
  export interface WorldStatePlugin {
    name: DevString;
    setup(build: Builder): void;
  }

  export function definePlugin(plugin: WorldStatePlugin): WorldStatePlugin {
    return plugin;
  }

  export interface Builder {
    log: ILogger;
    onDestroy: IPool;
    store: JotaiStore;
    worldAtom: Atom<WorldState | undefined>;
    addPlugin(plugin: WorldStatePlugin): void;
    addUnique<T extends World.AnyUniqueClass>(unique: T, value: T["CreationType"]): void;
    getUniqueAtom<T extends World.AnyUniqueClass>(unique: T): Atom<T["Type"] | undefined>;
    getUniqueOrThrow<T extends World.AnyUniqueClass>(unique: T): T["Type"];
    getComponentAtom<T extends World.AnyComponentClass>(uid: UID, component: T): Atom<T["Type"] | undefined>;
    getComponentAtom<T extends World.AnyComponentClass>(component: T): (uid: UID) => Atom<T["Type"] | undefined>;
    onEntityCreated<Options extends AnyEntityCreatedFnOptions>(
      options: Options,
      callback: EntityCreatedCallback<NoInfer<Options>>,
    ): void;
    watch<T>(atom: Atom<T>, fn: (value: T) => void, eq?: (a: T, b: T) => boolean): void;
  }

  function test() {
    class CDep1 extends World.Component("dep1")<CDep1, {}>() {}
    class CDep2 extends World.Component("dep2")<CDep2, {}>() {}
    class CTest1 extends World.Component("test1")<CTest1, {}>() {}
    class CTest2 extends World.Component("test2")<CTest2, {}>() {}
    type A = World.ComponentValues<[typeof CDep1, typeof CDep2]>;
    const em: Builder = null!;
    em.onEntityCreated(
      {
        requires: [CDep1],
        optional: [CDep2],
        provides: [CTest1, CTest2],
      },
      (uid, { dep1, dep2 }, world, onDeath) => ({
        test1: CTest1.of({}),
        test2: CTest2.of({}),
      }),
    );
  }
}

//
// -------------------------------------------------------------------------------------
// Section 5: EntityInfo, WorldState Interface, and UID Exports
// -------------------------------------------------------------------------------------

type EntityInfo = {
  _id: UID;
  _name: string;
  _source: DevString;
  _onDeath: IPool;
};

/**
 * WorldState manages entities, components, and uniques using Jotai.
 * Core ECS interface for managing game objects and their behavior.
 * @example
 * const world = new WorldStateImpl({ store, pool, plugins: [] });
 *
 * // Add entity with components:
 * const playerUID = world.addEntity(uid, PlayerEntity, {
 *   moveable: CMoveable.of({ maxSpeedAtom: atom(100) }),
 *   health: CHealth.of({ currentAtom: atom(100) })
 * });
 *
 * // Access components:
 * const health = world.getComponentMaybe(playerUID, CHealth);
 * const settings = world.getUniqueOrThrow(UGameSettings);
 */
export interface WorldState {
  readonly _dev: { uniques: unknown };
  readonly store: JotaiStore;
  readonly pool: DisposePool;

  getEntity<T extends World.AnyTarget>(id: UID<T>): World.EntityBundle<T>;
  getEntity<T extends World.AnyTarget, C extends World.AnyComponents | undefined>(
    id: UID<T>,
    components: C,
  ): World.EntityBundle<T> & World.EntityBundle<C>;

  getEntityMaybe<T extends World.AnyTarget>(id: UID<T>): World.EntityBundle<T> | undefined;
  getEntityMaybe<T extends World.AnyTarget, C extends World.AnyComponents>(
    id: UID<T>,
    components: C,
  ): (World.EntityBundle<T> & World.EntityBundle<C>) | undefined;

  addEntity<T extends World.AnyEntityClass>(
    id: UID<undefined>,
    entityClass: T,
    components: World.EntityCreationValues<T>,
  ): UID<T>;

  getComponentAtom<T extends World.AnyComponentClass>(uid: UID, component: T): Atom<T["Type"] | undefined>;
  getComponentAtom<T extends World.AnyComponentClass>(component: T): (uid: UID) => Atom<T["Type"] | undefined>;

  getUniqueOrThrow<T extends World.AnyUniqueClass>(unique: T): T["Type"];

  getComponentMaybe<T extends World.AnyComponentClass>(uid: UID, component: T): T["Type"] | undefined;

  removeEntity(uid: UID): void;
}

export type UID<TargetEntity extends World.AnyTarget = undefined> = World.UID<TargetEntity>;
export const uid = World.uid;
export namespace UID {
  export const withItemID = World.UID.withLocalID;
}
